import { useEffect, useState, useCallback } from 'react';
import api from '../api/client';

/**
 * Module-level listeners so every `useLastSeen()` hook instance in the tree
 * refreshes its state when ANY of them calls `markSeen(...)`. Without this,
 * a child component calling `useAutoMarkSeen('learners')` wouldn't update the
 * parent table's `isNew()` result, leaving the "NOUVEAU" badge stuck on screen.
 */
const listeners = new Set();
const notifyAll = () => listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });

/**
 * Exposes the `last_seen` map (per sidebar key) for highlighting NEW rows in tables.
 * The map uses keys like 'learners', 'companies', 'quotes_pending', 'invoices_unpaid', 'sessions_active'
 * and values are ISO timestamps — any row with `created_at > last_seen[key]` is NEW.
 *
 * Also exposes `markSeen(key)` which resets the NEW count for that section
 * (typically called after the user has inspected the table for a few seconds).
 */
export function useLastSeen() {
  const [lastSeen, setLastSeen] = useState({});
  const [counts, setCounts] = useState({});

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get('/sidebar/counts');
      setLastSeen(data.last_seen || {});
      setCounts(data);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    listeners.add(refresh);
    refresh();
    return () => { listeners.delete(refresh); };
  }, [refresh]);

  const markSeen = useCallback(async (key) => {
    try {
      await api.post(`/sidebar/mark-seen/${key}`);
      notifyAll();
    } catch {
      /* ignore */
    }
  }, []);

  const isNew = useCallback((key, createdAt) => {
    if (!createdAt) return false;
    const since = lastSeen[key];
    if (!since) return true; // never seen → all items are "new"
    try {
      return new Date(createdAt) > new Date(since);
    } catch {
      return false;
    }
  }, [lastSeen]);

  return { lastSeen, counts, markSeen, isNew, refresh };
}

/**
 * Auto mark-seen: fires `markSeen(key)` after `delay` ms on mount.
 * Use this in each page with a sidebar badge so the pastille resets after
 * the user has had a chance to look at new rows.
 */
export function useAutoMarkSeen(key, delay = 3000) {
  const { markSeen } = useLastSeen();
  useEffect(() => {
    const t = setTimeout(() => { markSeen(key); }, delay);
    return () => clearTimeout(t);
  }, [key, delay, markSeen]);
}
